import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, RotateCcw, Trash2 } from 'lucide-react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import JobLogsPanel from '../components/JobLogsPanel'
import LoadingState from '../components/ui/LoadingState'
import PageShell from '../components/ui/PageShell'
import PipelineVisualizer from '../components/PipelineVisualizer'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import { ui } from '../styles/ui'
import queueService from '../services/queueService'
import { useTranslation } from '../contexts/TranslationContext'
import { formatDateTime } from '../utils/date'
import { formatEmptyValue } from '../utils/displayValue'

const TABS = ['overview', 'logs', 'pipeline']

const STATUS_BADGE = {
  completed: ui.badgeSuccess,
  failed: ui.badgeError,
  pending: ui.badgeNeutral,
  processing: ui.badgeWarning,
}

export default function JobDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [job, setJob] = useState(null)
  const [logs, setLogs] = useState([])
  const [pipeline, setPipeline] = useState([])
  const [loading, setLoading] = useState(true)
  const [logsLoading, setLogsLoading] = useState(false)
  const [pipelineLoading, setPipelineLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [error, setError] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [confirmRemove, setConfirmRemove] = useState(false)

  useEffect(() => {
    loadJob()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (activeTab === 'logs' && logs.length === 0) loadLogs()
    if (activeTab === 'pipeline' && pipeline.length === 0) loadPipeline()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  function loadJob() {
    setLoading(true)
    setError(null)
    queueService.getJob(id)
      .then(res => setJob(res.data?.data || null))
      .catch(() => setError(t('Failed to load job')))
      .finally(() => setLoading(false))
  }

  function loadLogs() {
    setLogsLoading(true)
    queueService.getJobLogs(id)
      .then(res => setLogs(res.data?.data || []))
      .catch(() => setLogs([]))
      .finally(() => setLogsLoading(false))
  }

  function loadPipeline() {
    setPipelineLoading(true)
    queueService.getJobPipeline(id)
      .then(res => setPipeline(res.data?.data || []))
      .catch(() => setPipeline([]))
      .finally(() => setPipelineLoading(false))
  }

  function handleRetry() {
    setActionError(null)
    queueService.retryJob(id)
      .then(() => loadJob())
      .catch(() => setActionError(t('Failed to retry job')))
  }

  function handleReplay() {
    setActionError(null)
    queueService.replayJob(id)
      .then(() => loadJob())
      .catch(() => setActionError(t('Failed to replay job')))
  }

  function handleRemove() {
    setActionError(null)
    queueService.removeJob(id)
      .then(() => navigate('/admin/queue-workers'))
      .catch(() => setActionError(t('Failed to remove job')))
  }

  if (loading) {
    return (
      <PageShell><LoadingState /></PageShell>
    )
  }

  if (error || !job) {
    return (
      <PageShell>
        <div className={ui.alertInfo}>{error || t('Job not found')}</div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={`${ui.buttonGhost} gap-1`}
        >
          <ArrowLeft size={16} />
          {t('Back')}
        </button>
        <h1 className={`${ui.title} flex-1`}>{t('Job detail')} <span className="font-mono text-base text-muted-foreground ml-2">{id}</span></h1>
        <div className="flex gap-2">
          <Button onClick={loadJob} title={t('Refresh')}>
            <RefreshCw size={14} />
          </Button>
          <button type="button" className={ui.buttonGhost} onClick={handleReplay}>
            <RotateCcw size={14} className="mr-1" />
            {t('Replay')}
          </button>
          <button type="button" className={ui.buttonSmDanger} onClick={handleRetry}>
            <RefreshCw size={14} className="mr-1" />
            {t('Retry')}
          </button>
          <button type="button" className={ui.buttonDanger} onClick={() => setConfirmRemove(true)}>
            <Trash2 size={14} className="mr-1" />
            {t('Remove')}
          </button>
        </div>
      </div>

      {actionError && <div className={ui.alertError}>{actionError}</div>}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-outline">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition ${
              activeTab === tab
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'overview'
              ? t('job_detail.tab.overview', 'Overview')
              : tab === 'logs'
                ? t('job_detail.tab.logs', 'Logs')
                : tab === 'pipeline'
                  ? t('job_detail.tab.pipeline', 'Pipeline')
                  : tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <div className={ui.label}>{t('ID')}</div>
              <div className="mt-1 font-mono text-sm text-foreground break-all">{job.id}</div>
            </div>
            <div>
              <div className={ui.label}>{t('Source')}</div>
              <div className="mt-1 text-sm text-foreground">{job.source}</div>
            </div>
            <div>
              <div className={ui.label}>{t('Status')}</div>
              <div className="mt-1">
                <span className={`${ui.badge} ${STATUS_BADGE[job.status] ?? ui.badgeNeutral}`}>
                  {job.status}
                </span>
              </div>
            </div>
            <div>
              <div className={ui.label}>{t('OCR status')}</div>
              <div className="mt-1">
                {job.ocrStatus
                  ? <span className={`${ui.badge} ${STATUS_BADGE[job.ocrStatus] ?? ui.badgeNeutral}`}>{job.ocrStatus}</span>
                  : <span className="text-muted-foreground text-sm">{formatEmptyValue(null)}</span>}
              </div>
            </div>
            <div>
              <div className={ui.label}>{t('Product analysis')}</div>
              <div className="mt-1">
                {job.productAnalysisStatus
                  ? <span className={`${ui.badge} ${STATUS_BADGE[job.productAnalysisStatus] ?? ui.badgeNeutral}`}>{job.productAnalysisStatus}</span>
                  : <span className="text-muted-foreground text-sm">{formatEmptyValue(null)}</span>}
              </div>
            </div>
            <div>
              <div className={ui.label}>{t('Translation')}</div>
              <div className="mt-1">
                {job.productTranslationStatus
                  ? <span className={`${ui.badge} ${STATUS_BADGE[job.productTranslationStatus] ?? ui.badgeNeutral}`}>{job.productTranslationStatus}</span>
                  : <span className="text-muted-foreground text-sm">{formatEmptyValue(null)}</span>}
              </div>
            </div>
            <div>
              <div className={ui.label}>{t('QR extraction')}</div>
              <div className="mt-1">
                {job.qrCodeProductExtractionStatus
                  ? <span className={`${ui.badge} ${STATUS_BADGE[job.qrCodeProductExtractionStatus] ?? ui.badgeNeutral}`}>{job.qrCodeProductExtractionStatus}</span>
                  : <span className="text-muted-foreground text-sm">{formatEmptyValue(null)}</span>}
              </div>
            </div>
            <div>
              <div className={ui.label}>{t('Created at')}</div>
              <div className="mt-1 text-sm text-foreground">{job.createdAt ? formatDateTime(job.createdAt) : formatEmptyValue(null)}</div>
            </div>
            <div>
              <div className={ui.label}>{t('Updated at')}</div>
              <div className="mt-1 text-sm text-foreground">{job.updatedAt ? formatDateTime(job.updatedAt) : formatEmptyValue(null)}</div>
            </div>
            {job.qrCodeLink && (
              <div className="col-span-full">
                <div className={ui.label}>{t('QR code link')}</div>
                <div className="mt-1 break-all text-sm text-blue-600">
                  <a href={job.qrCodeLink} target="_blank" rel="noopener noreferrer">{job.qrCodeLink}</a>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'logs' && (
        <JobLogsPanel logs={logs} loading={logsLoading} />
      )}

      {activeTab === 'pipeline' && (
        <PipelineVisualizer steps={pipeline} loading={pipelineLoading} />
      )}

      {/* Remove confirmation dialog */}
      <ConfirmDialog
        open={confirmRemove}
        title={t('Remove job?')}
        message={t('This will permanently delete the receipt and all its data. This cannot be undone.')}
        confirmText={t('Remove')}
        onConfirm={() => { setConfirmRemove(false); handleRemove() }}
        onCancel={() => setConfirmRemove(false)}
      />
    </PageShell>
  )
}

